import api, { ApiResponse } from './apiClient';

export interface TemporaryCodeUsedBy {
  userId: {
    id: string;
    name: string;
    email: string;
  };
  usedAt: string;
  email: string;
}

export interface TemporaryCode {
  id: string;
  code: string;
  company: {
    id: string;
    name: string;
  };
  role: string;
  description?: string;
  maxUses: number;
  currentUses: number;
  usesRemaining: number;
  isActive: boolean;
  createdBy: {
    id: string;
    name: string;
  };
  createdAt: string;
  expiresAt: string;
  usedBy?: TemporaryCodeUsedBy[];
  isExpired?: boolean;
  isExhausted?: boolean;
  isAvailable?: boolean;
  /** Who this code was generated for. */
  preloadedData?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    cedula?: string;
  };
  /** Null/undefined when the invitation email was never actually sent
   *  (code created but not yet dispatched). */
  invitationSentAt?: string;
}

export interface BatchStatistics {
  totalInvitations?: number;
  successfulCodes?: number;
  usersRegistered?: number;
}

export interface BatchEmailStatus {
  sent?: number;
  failed?: number;
}

export interface InvitationBatch {
  id: string;
  batchId: string;
  description?: string;
  batchStatus?: string;
  status?: string;
  totalInvitations?: number;
  statistics?: BatchStatistics;
  emailStatus?: BatchEmailStatus;
  createdBy?: {
    name?: string;
    email?: string;
  };
  createdAt: string;
  scheduledFor?: string;
}

export interface BatchDetailCode {
  id: string;
  code: string;
  company: string;
  invitedUser?: {
    name?: string;
    email?: string;
  };
  status?: {
    isExpired?: boolean;
    isActive?: boolean;
    isUsed?: boolean;
  };
}

export interface BatchDetailSummary {
  total?: number;
  used?: number;
  emailsSent?: number;
  expired?: number;
  byCompany?: Record<string, { total: number; used: number; sent: number }>;
}

export interface BatchDetail {
  batch?: {
    batchId: string;
  };
  summary?: BatchDetailSummary;
  detailedCodes?: BatchDetailCode[];
}

export interface TemporaryCodesListParams {
  page: number;
  limit: number;
}

export interface TemporaryCodesListResponse {
  codes: TemporaryCode[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface BatchesListParams {
  page: number;
  limit: number;
  includeStats?: boolean;
}

export interface BatchesListResponse {
  batches: InvitationBatch[];
  pagination?: {
    total: number;
  };
}

class TemporaryCodesApi {
  /**
   * Get supervised companies for the current user
   */
  async getSupervisedCompanies(): Promise<ApiResponse<any>> {
    return api.get('/companies/supervised');
  }

  /**
   * List temporary codes with pagination
   */
  async listCodes(params: TemporaryCodesListParams & { companyId?: string; isActive?: boolean; pending?: boolean; includeExpired?: boolean }): Promise<ApiResponse<TemporaryCodesListResponse>> {
    const query: Record<string, string> = {
      page: params.page.toString(),
      limit: params.limit.toString(),
    };
    if (params.companyId) query.companyId = params.companyId;
    if (params.isActive !== undefined) query.isActive = String(params.isActive);
    if (params.pending !== undefined) query.pending = String(params.pending);
    if (params.includeExpired !== undefined) query.includeExpired = String(params.includeExpired);
    return api.get('/temporary-codes', query);
  }

  /**
   * Get temporary code statistics
   */
  async getStatistics(): Promise<ApiResponse<any>> {
    return api.get('/temporary-codes/statistics');
  }

  /**
   * Delete a temporary code by ID — revokes the invitation.
   */
  async deleteCode(codeId: string): Promise<ApiResponse<any>> {
    return api.delete(`/temporary-codes/${codeId}`);
  }

  /**
   * Resend the invitation email for an existing temporary code. The
   * code stays the same; only the email is re-dispatched and the
   * `invitationSentAt` timestamp is bumped.
   */
  async resendInvitation(codeId: string): Promise<ApiResponse<any>> {
    return api.post(`/temporary-codes/${codeId}/resend`);
  }

  /**
   * Edit a pending (unredeemed) invitation's info. Issues a brand new code
   * with the updated data and invalidates the old one — never mutates the
   * existing code in place, so the old row stays around (isActive: false)
   * as a record of what it was superseded by.
   */
  async reissueCode(codeId: string, data: {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    phone?: string;
    cedula?: string;
    expiresAt?: string;
  }): Promise<ApiResponse<{ newCodeId: string; code: string; expiresAt: string }>> {
    return api.put(`/temporary-codes/${codeId}/reissue`, data);
  }

  /**
   * List batches with pagination
   */
  async listBatches(params: BatchesListParams): Promise<ApiResponse<BatchesListResponse>> {
    return api.get('/temporary-codes/batches', {
      page: params.page.toString(),
      limit: params.limit.toString(),
      includeStats: params.includeStats ? 'true' : undefined,
    });
  }

  /**
   * Get batch details by ID
   */
  async getBatchDetails(batchId: string): Promise<ApiResponse<any>> {
    return api.get(`/temporary-codes/batches/${batchId}`);
  }

  /**
   * Execute a batch
   */
  async executeBatch(batchId: string): Promise<ApiResponse<any>> {
    return api.post(`/temporary-codes/batches/${batchId}/execute`);
  }

  /**
   * Pause a batch
   */
  async pauseBatch(batchId: string): Promise<ApiResponse<any>> {
    return api.post(`/temporary-codes/batches/${batchId}/pause`);
  }

  /**
   * Cancel a batch
   */
  async cancelBatch(batchId: string): Promise<ApiResponse<any>> {
    return api.post(`/temporary-codes/batches/${batchId}/cancel`);
  }

  /**
   * Create a single temporary code
   */
  async createCode(data: {
    companyId: string;
    role: string;
    description?: string;
    maxUses?: number;
    expiresAt?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<any>> {
    return api.post('/temporary-codes', data);
  }

  /**
   * Schedule a batch of invitations
   */
  async scheduleBatch(data: {
    description: string;
    sendEmails: boolean;
    invitations: Array<{
      firstName: string;
      lastName: string;
      email: string;
      role: string;
      companyId: string;
      phone?: string;
      cedula?: string;
      ordenPatronal?: string;
      polizaINS?: string;
    }>;
  }): Promise<ApiResponse<any>> {
    return api.post('/temporary-codes/batches/schedule', data);
  }

  /**
   * Validate a temporary code (public endpoint, no auth required).
   *
   * Returns minimal info only — company name, role, usesRemaining, expiresAt.
   * The previous `includeFullData` flag returned the code's `preloadedData`
   * (firstName, lastName, email, phone, cedula, ordenPatronal, polizaINS),
   * which let any caller who could guess a 6-char code enumerate user PII.
   * That branch was removed on the backend; the parameter is accepted here
   * as a no-op so existing call sites keep compiling, but it has no effect.
   */
  async validateCode(code: string, _includeFullData?: boolean): Promise<ApiResponse<any>> {
    return api.post('/temporary-codes/validate', { code }, { skipAuth: true });
  }
}

export const temporaryCodesApi = new TemporaryCodesApi();
export default temporaryCodesApi;
